/**
 * UpcomingClassesCard - Shows upcoming scheduled classes
 */

import { Link } from 'react-router-dom';
import type { UpcomingClass } from '../../api/studios';
import { daysUntil } from '../../api/studios';
import { formatTimeDisplay } from '@sda/shared';

interface UpcomingClassesCardProps {
  classes: UpcomingClass[];
  isLoading?: boolean;
}

export function UpcomingClassesCard({ classes, isLoading }: UpcomingClassesCardProps) {
  if (isLoading) {
    return (
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-warm-900">Upcoming Classes</h3>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-20 bg-warm-100 rounded-lg" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-warm-900">Upcoming Classes</h3>
        <Link
          to="/dashboard/bookings"
          className="text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          View all
        </Link>
      </div>

      {classes.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-warm-100 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-warm-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
          <p className="text-warm-600 mb-4">No upcoming classes scheduled</p>
          <Link to="/events" className="btn-primary py-2 px-4 inline-block">
            Find Shows Near You
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {classes.map((cls) => {
            const days = daysUntil(cls.scheduled_date);
            const dateObj = new Date(cls.scheduled_date + 'T12:00:00');
            const formattedDate = dateObj.toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
            });

            return (
              <Link
                key={cls.id}
                to={`/dashboard/bookings/${cls.booking_id}`}
                className="block p-4 rounded-lg border border-warm-200 hover:border-primary-300 hover:bg-primary-50/50 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-warm-900 truncate">
                        {cls.show_name}
                      </span>
                      {days <= 7 && (
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            days <= 2
                              ? 'bg-danger-100 text-danger-700'
                              : 'bg-warning-100 text-warning-700'
                          }`}
                        >
                          {days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `${days} days`}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-warm-600">{cls.class_name}</p>
                    <div className="mt-2 flex items-center gap-4 text-sm text-warm-500">
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        {formattedDate}
                      </span>
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        {formatTimeDisplay(cls.start_time)}
                      </span>
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <div className="text-sm text-warm-500">
                      {[cls.city, cls.state].filter(Boolean).join(', ')}
                    </div>
                    {cls.faculty_name && (
                      <div className="mt-1 text-sm text-primary-600 font-medium">
                        {cls.faculty_name}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default UpcomingClassesCard;
